import React, { createContext, useContext, useState, ReactNode } from 'react';
import { services } from '../services';
import { UserStatus, UserRole } from '../types/user';

interface UserApiResponse {
  _id: string;
  name?: string;
  email?: string;
  walletAddress: string;
  status: UserStatus;
  role:
    | {
        id?: string;
        roleName: string;
      }
    | UserRole;
}

interface AssetApiResponse {
  _id: string;
  name?: string;
  assetAddress?: string;
  description?: string;
  assetType?: string;
  status?: string;
}

interface SearchMetadata {
  status?: UserStatus | string;
  role?: UserRole | string;
  type?: string;
  amount?: string;
  [key: string]: unknown;
}

interface SearchResult {
  id: string;
  type: 'user' | 'asset' | 'transaction';
  title: string;
  subtitle: string;
  url: string;
  metadata?: SearchMetadata;
}

interface SearchContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: SearchResult[];
  setSearchResults: (results: SearchResult[]) => void;
  isSearching: boolean;
  setIsSearching: (searching: boolean) => void;
  performSearch: (query: string) => Promise<void>;
  clearSearch: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchQuery(query);

    try {
      // This will be implemented with actual API calls
      const results: SearchResult[] = [];

      // Search users
      const userResults = await searchUsers(query);
      results.push(...userResults);

      // Search assets
      const assetResults = await searchAssets(query);
      results.push(...assetResults);

      // Search transactions (if available)
      const transactionResults = await searchTransactions(query);
      results.push(...transactionResults);

      setSearchResults(results);
    } catch (error) {
      setSearchResults([]);
      if (process.env.NODE_ENV === 'development') {
        console.error('[SearchContext] Search failed:', error);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  };

  const searchUsers = async (query: string): Promise<SearchResult[]> => {
    try {
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: UserApiResponse[];
      }>(`/user/list?search=${encodeURIComponent(query)}&limit=5&page=1`);

      if (response.data?.status && response.data?.data) {
        return response.data.data.map((user: UserApiResponse) => ({
          id: user._id,
          type: 'user' as const,
          title: user.name || 'Unknown User',
          subtitle: user.email || user.walletAddress || '',
          url: `/users/${user._id}`,
          metadata: {
            status: user.status,
            role: (typeof user.role === 'object' ? user.role?.roleName : user.role) || 'USER',
          },
        }));
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const searchAssets = async (query: string): Promise<SearchResult[]> => {
    try {
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: AssetApiResponse[];
      }>(`/asset?search=${encodeURIComponent(query)}&limit=5&page=1`);

      if (response.data?.status && response.data?.data) {
        return response.data.data.map((asset: AssetApiResponse) => ({
          id: asset._id,
          type: 'asset' as const,
          title: asset.name || 'Unknown Asset',
          subtitle: asset.assetAddress || asset.description || '',
          url: `/assets/${asset._id}`,
          metadata: { type: asset.assetType, status: asset.status },
        }));
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const searchTransactions = async (_query: string): Promise<SearchResult[]> => {
    // Note: Transaction search might not be implemented in the backend yet
    // This is a placeholder for future implementation
    try {
      // For now, return empty array as transaction search may not be available
      return [];
    } catch (error) {
      return [];
    }
  };

  const value: SearchContextType = {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearching,
    setIsSearching,
    performSearch,
    clearSearch,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};
